import { Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Author, KudosRecipients } from "@teammood/timeline";
import { UserService } from "@teammood/shared/util/user";

@Component({
    selector: "tm-kudos-recipients-display",
    templateUrl: "./kudos-recipients-display.component.html",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule
    ]
})
export class KudosRecipientsDisplayComponent implements OnInit {

    @Input({required: true})
    recipients!: KudosRecipients;

    @Input({required: true})
    author!: Author;

    protected myUserId: string;

    constructor(private userService: UserService, private translate: TranslateService) {
    }

    ngOnInit() {
        this.userService.get().then(user => {
            this.myUserId = user.id;
        });
    }

    protected formatAsHTML(recipients: KudosRecipients, author: Author, connectedUserId : string): string {
        if(recipients.selectedTeammates.length === 0 && !recipients.allTeamSelected) {
            const placeholderRecipents: KudosRecipients = new KudosRecipients();
            placeholderRecipents.selectedTeammates.push({ id: '123', fullname: 'Recipient' });
            return '<span class="kudos-people kudos-placeholder-people me-1">'+ this.translate.instant('kudos.card.placeholder_recipients') +'</span>' + this.formatDescription(placeholderRecipents, author, connectedUserId);
        }

        const badgeOpeningTag = '<span class="kudos-people me-1">';
        const badgeClosingTag = '</span>'

        const recipientsAndDescription = this.formatRecipients(recipients, connectedUserId).map(recipient => badgeOpeningTag + recipient + badgeClosingTag).join(' ') + ' ' + this.formatDescription(recipients, author, connectedUserId)

        const isAuthor = author.id === connectedUserId;

        if (isAuthor) {
            return recipientsAndDescription;
        } else {
            return recipientsAndDescription + ' ' + badgeOpeningTag + this.formatAuthor(author, connectedUserId) + badgeClosingTag
        }
    }

    private isUniqueRecipient(recipients: KudosRecipients, connectedUserId: string): boolean {
        return (recipients.selectedTeammates.filter(recipient => {
            return recipient.id === connectedUserId;
        }).length > 0 && recipients.selectedTeammates.length === 1);
    }

    private isOneOfTheRecipients(recipients: KudosRecipients, connectedUserId: string): boolean {
        return (recipients.selectedTeammates.filter(recipient => {
            return recipient.id === connectedUserId;
        }).length > 0 && recipients.selectedTeammates.length > 1);
    }

    formatAsString(recipients: KudosRecipients, author: Author, connectedUserId : string): string {

        return (this.formatRecipients(recipients, connectedUserId).join(' ') + ' ' + this.formatDescription(recipients, author, connectedUserId) + ' ' + this.formatAuthor(author, connectedUserId)).trim()
    }

    private formatRecipients(recipients: KudosRecipients, connectedUserId: string): string[] {

        if (this.isUniqueRecipient(recipients, connectedUserId)) {
            return [this.translate.instant('kudos.card.recipient.you')];
        } else if (recipients.allTeamSelected) {
            return [this.translate.instant('kudos.card.recipient.all')];
        } else if (this.isOneOfTheRecipients(recipients, connectedUserId)) {
            return [this.translate.instant('kudos.card.recipient.you')].concat(recipients.selectedTeammates.filter(recipient => recipient.id !== connectedUserId).map(recipients => recipients.fullname));
        } else {
            return recipients.selectedTeammates.map(recipients => recipients.fullname);
        }
    }

    private formatDescription(recipients: KudosRecipients, author: Author, connectedUserId: string): string {

        const isAuthor = author.id === connectedUserId;

        if(recipients.allTeamSelected && !isAuthor){
            return this.translate.instant('kudos.card.description');
        } else if(recipients.allTeamSelected && isAuthor){
            return this.translate.instant('kudos.card.author.you');
        } else if(this.isUniqueRecipient(recipients, connectedUserId)){
            return this.translate.instant('kudos.card.description.you');
        } else if(this.isOneOfTheRecipients(recipients, connectedUserId)){
            return this.translate.instant('kudos.card.description.you_multiple');
        } else if(recipients.selectedTeammates.length === 1 && isAuthor){
            return this.translate.instant('kudos.card.author.you');
        } else if(recipients.selectedTeammates.length > 1 && isAuthor){
            return this.translate.instant('kudos.card.author.you_multiple');
        } else if(recipients.selectedTeammates.length === 1){
            return this.translate.instant('kudos.card.description');
        } else if(recipients.selectedTeammates.length > 1){
            return this.translate.instant('kudos.card.description_plural');
        }
    }

    private formatAuthor(author: Author, connectedUserId: string): string {

        const isAuthor = author.id === connectedUserId;

        if (isAuthor){
            return '';
        } else {
            return author.fullname;
        }
    }
}